import { useEffect, useState } from 'react'

const DESKTOP_MIN_WIDTH = 1200

const query = () =>
  typeof window === 'undefined' ? false : window.innerWidth >= DESKTOP_MIN_WIDTH

// One resize listener and one timer for the whole app, not one per caller.
// useDimensions is used from ~80 modules, and GameImage calls it *per tile* —
// so a 100+ tile casino grid registered 100+ window resize listeners, each
// with its own trailing timer and its own setState. Android fires `resize`
// when the URL bar collapses during a fling, so all of them fired on the
// scroll path at once.
let current = query()
let timer: ReturnType<typeof setTimeout> | null = null
const subscribers = new Set<(isDesktop: boolean) => void>()

const onResize = () => {
  if (timer) return
  // Trailing throttle: the shared utils/common throttle drops the last event
  // of a burst, which left isDesktop stale at the width where the user's
  // drag/rotation ended. Deferring to a timer guarantees the final resize
  // event is always applied.
  timer = setTimeout(() => {
    timer = null
    const next = query()
    // Only wake subscribers when the breakpoint is actually crossed. Most
    // resizes on mobile are chrome collapsing, not a layout change.
    if (next === current) return
    current = next
    subscribers.forEach(notify => notify(next))
  }, 300)
}

const subscribe = (notify: (isDesktop: boolean) => void) => {
  if (subscribers.size === 0) {
    // Nothing has been listening, so `current` may predate the last resize.
    current = query()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize)
    }
  }
  subscribers.add(notify)

  return () => {
    subscribers.delete(notify)
    if (subscribers.size > 0) return
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', onResize)
    }
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
}

const useDimensions = () => {
  const [isDesktop, setIsDesktop] = useState(current)

  useEffect(() => {
    const unsubscribe = subscribe(setIsDesktop)
    // `current` is re-read on the first subscribe, so adopt it if this
    // component rendered against a stale value.
    if (current !== isDesktop) setIsDesktop(current)
    // Subscribing once per mount is the point; isDesktop is read, not tracked.
    return unsubscribe
  }, [])

  return { isDesktop }
}

export default useDimensions
